import { CONTENT_WIDGETS, type FieldDef, type FormSchema, type SectionMeta } from '../types';

import fieldToProperty from './fieldToProperty';

// Only keep sections that still have at least one field pointing at them, and
// emit them in first-appearance order so grouping survives field reordering.
const usedSections = (fields: FieldDef[], sections: SectionMeta[]): SectionMeta[] => {
  const present = new Set(fields.map((field) => field.sectionKey).filter(Boolean));
  const byKey = new Map(sections.map((section) => [section.key, section]));
  const ordered: SectionMeta[] = [];
  const seen = new Set<string>();
  for (const field of fields) {
    const key = field.sectionKey;
    if (key && present.has(key) && !seen.has(key) && byKey.has(key)) {
      seen.add(key);
      ordered.push(byKey.get(key) as SectionMeta);
    }
  }
  return ordered;
};

const toJsonSchema = (fields: FieldDef[], sections: SectionMeta[] = []): FormSchema => {
  const ui: FormSchema['x-ui'] = { order: fields.map((field) => field.key) };
  const activeSections = usedSections(fields, sections);
  if (activeSections.length > 0) {
    ui.sections = activeSections.map((section) => ({
      key: section.key,
      title: section.title,
      ...(section.sectionId ? { sectionId: section.sectionId } : {})
    }));
  }

  return {
    type: 'object',
    properties: Object.fromEntries(fields.map((field) => [field.key, fieldToProperty(field)])),
    required: fields
      .filter((field) => field.required && !CONTENT_WIDGETS.includes(field.widget))
      .map((field) => field.key),
    additionalProperties: false,
    'x-ui': ui
  };
};

export default toJsonSchema;
